import React, { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const data = [
  { month: "Apr \'25", aum: 2100, deployed: 1520, distributions: 18 },
  { month: "May \'25", aum: 2150, deployed: 1580, distributions: 22 },
  { month: "Jun \'25", aum: 2180, deployed: 1610, distributions: 19 },
  { month: "Jul \'25", aum: 2210, deployed: 1650, distributions: 25 },
  { month: "Aug \'25", aum: 2240, deployed: 1680, distributions: 21 },
  { month: "Sep \'25", aum: 2280, deployed: 1710, distributions: 28 },
  { month: "Oct \'25", aum: 2310, deployed: 1740, distributions: 24 },
  { month: "Nov \'25", aum: 2350, deployed: 1770, distributions: 30 },
  { month: "Dec \'25", aum: 2390, deployed: 1790, distributions: 35 },
  { month: "Jan \'26", aum: 2420, deployed: 1810, distributions: 20 },
  { month: "Feb \'26", aum: 2450, deployed: 1820, distributions: 22 },
  { month: "Mar \'26", aum: 2470, deployed: 1830, distributions: 94 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    return (
      <div
        className="bg-popover border border-border rounded-lg shadow-md px-4 py-3"
        style={{ minWidth: 180 }}
      >
        <p className="font-caption font-semibold text-foreground text-sm mb-2">{label}</p>
        {payload?.map((p) => (
          <div key={p?.dataKey} className="flex items-center justify-between gap-4 mb-1">
            <span className="font-caption text-muted-foreground text-xs">{p?.name}</span>
            <span className="font-data font-medium text-foreground text-xs">
              £{p?.value?.toLocaleString("en-GB")}M
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const tabs = ["12M", "6M", "3M"];

export default function PortfolioChart() {
  const [activeTab, setActiveTab] = useState("12M");

  const sliceMap = { "12M": 12, "6M": 6, "3M": 3 };
  const chartData = data?.slice(-sliceMap?.[activeTab]);

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: "1rem" }}>
            Portfolio Performance
          </h3>
          <p className="font-caption text-muted-foreground text-xs mt-0.5">
            AUM, deployed capital &amp; distributions (£M)
          </p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {tabs?.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-1.5 rounded-md font-caption font-medium text-xs transition-all duration-200 ${
                activeTab === t
                  ? "bg-card text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="px-2 py-4" style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="aumGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="deployedGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-secondary)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--color-secondary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `£${v}M`}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: "0.75rem", fontFamily: "var(--font-caption)", paddingTop: 8 }}
            />
            <Area
              type="monotone"
              dataKey="aum"
              name="AUM"
              stroke="var(--color-primary)"
              strokeWidth={2}
              fill="url(#aumGrad)"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="deployed"
              name="Deployed"
              stroke="var(--color-secondary)"
              strokeWidth={2}
              fill="url(#deployedGrad)"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}